import { success3dIcon, useIntl, useSetPageTitle } from '@capsicum/common';
import {
	Alert,
	Button,
	GenericDrawer,
	ModalService,
	Skeleton,
	SkeletonItem,
	Text,
	TextInput,
	useRouter
} from '@capsicum/common-web';
import { PasswordClosedIcon, PasswordIcon, WarningIcon } from '@capsicum/common-web/icons';
import { useWithdrawalPassword } from 'libs/features/account/shared/src/hooks';
import { useCallback, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import styled from 'styled-components';

export const WithdrawalPasswordPage = () => {
	const { $t } = useIntl();
	const { back } = useRouter();
	const {
		formProvider,
		isSubmitLoading,
		isWithdrawalPasswordExistenceCheckLoading,
		newWithdrawalPasswordForm,
		submitWithdrawalPassword,
		updateWithdrawalPasswordForm,
		withdrawalPasswordExists,
		withdrawalPasswordPageTexts
	} = useWithdrawalPassword();
	const [showCurrentPassword, setShowCurrentPassword] = useState<boolean>(false);
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [showConfirmPassword, setConfirmShowPassword] = useState<boolean>(false);

	useSetPageTitle(withdrawalPasswordPageTexts.pageTitle);

	const onSubmitSuccess = ({ description, title }: { description: string; title: string }) => {
		ModalService.addModal({
			detached: true,
			onDismiss: () => back(),
			renderComponent: () => (
				<GenericDrawer
					description={description}
					image={success3dIcon}
					testID="withdrawal-password-submit-success-modal"
					title={title}
				/>
			)
		});
	};

	const NewPasswordInputs = useCallback(
		() => (
			<>
				<TextInput
					autoCapitalize="none"
					iconEnd={{
						icon: showPassword ? PasswordIcon : PasswordClosedIcon,
						onClick: () => {
							setShowPassword(!showPassword);
						}
					}}
					label={withdrawalPasswordPageTexts.newPasswordLabel}
					name="newPassword"
					supportingText={$t('settings.withdrawalPassword.passwordRequirementsHint')}
					testID="withdrawal-password-form-new-password-input"
					type={showPassword ? 'text' : 'password'}
				/>
				<TextInput
					autoCapitalize="none"
					iconEnd={{
						icon: showConfirmPassword ? PasswordIcon : PasswordClosedIcon,
						onClick: () => {
							setConfirmShowPassword(!showConfirmPassword);
						}
					}}
					label={withdrawalPasswordPageTexts.confirmPasswordLabel}
					name="confirmNewPassword"
					testID="withdrawal-password-form-confirm-new-password-input"
					type={showConfirmPassword ? 'text' : 'password'}
				/>
			</>
		),
		[showPassword, showConfirmPassword, withdrawalPasswordExists]
	);

	if (isWithdrawalPasswordExistenceCheckLoading) {
		return (
			<S.StyledView>
				<S.Wrapper>
					<Skeleton height={56}>
						<SkeletonItem borderRadius={5} count={2} height={15} />
						<SkeletonItem borderRadius={10} />
						<SkeletonItem borderRadius={10} />
						<SkeletonItem borderRadius={30} height={35} />
					</Skeleton>
				</S.Wrapper>
			</S.StyledView>
		);
	}

	return (
		<S.StyledView>
			<S.Wrapper>
				<Text variant="text3">{$t('settings.withdrawalPassword.description')}</Text>
				{withdrawalPasswordExists ? (
					<FormProvider {...updateWithdrawalPasswordForm}>
						<TextInput
							autoCapitalize="none"
							iconEnd={{
								icon: showCurrentPassword ? PasswordIcon : PasswordClosedIcon,
								onClick: () => {
									setShowCurrentPassword(!showCurrentPassword);
								}
							}}
							label={$t('settings.withdrawalPassword.currentPassword')}
							name="currentPassword"
							testID="withdrawal-password-form-current-password-input"
							type={showCurrentPassword ? 'text' : 'password'}
						/>
						<NewPasswordInputs />
					</FormProvider>
				) : (
					<FormProvider {...newWithdrawalPasswordForm}>
						<NewPasswordInputs />
					</FormProvider>
				)}
				<Alert closable={false} icon={WarningIcon} testID="withdrawal-password-alert" type="important">
					{$t('settings.withdrawalPassword.warning')}
				</Alert>
				<Button
					disabled={!formProvider.formState.isValid}
					isLoading={isSubmitLoading}
					testID="withdrawal-password-form-submit-button"
					text={$t('global.button.submit')}
					onClick={() => submitWithdrawalPassword(onSubmitSuccess)}
				/>
			</S.Wrapper>
		</S.StyledView>
	);
};

namespace S {
	export const StyledView = styled.div`
		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			margin: ${({ theme }) => theme.spacings.small};
		}
		margin-top: ${({ theme }) => theme.spacings.medium};
		width: 100%;
	`;

	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.small};
		padding: ${({ theme }) => theme.spacings.small};
		border-radius: ${({ theme }) => theme.sizing.xxsmall};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
	`;
}
